// Availability Service for BookAPT Mobile App
// Handles time slot availability checking with worker-based capacity

import SimpleStorage from '../utils/simpleStorage';
import workerService, { Worker } from './workerService';
import { Platform } from 'react-native';

export interface TimeSlotWithWorkers {
  time: string;
  availableWorkers: Worker[];
  hasAvailability: boolean;
  totalCapacity: number;
  bookedCount: number;
}

export interface BookingConflict {
  businessId: string;
  appointmentDate: string;
  appointmentTime: string;
  workerId: string;
  status: string;
}

class AvailabilityService {
  private readonly ALL_BOOKINGS_KEY = 'all_bookings';
  
  /**
   * Get all bookings from storage (Firestore first, then local fallback)
   */
  async getAllBookings(): Promise<any[]> {
    try {
      // Try to load from Firestore first (most up-to-date)
      let loadedBookings: any[] = [];
      let loadedFromFirestore = false;
      
      if (Platform.OS === 'web') {
        try {
          // Use require() to ensure same module instance as unifiedAuthService
          const { collection, getDocs } = require('firebase/firestore');
          const { getWebDb, initializeFirebase } = require('./unifiedAuthService');
          
          // Ensure Firebase is initialized and get named database
          const initialized = initializeFirebase();
          if (!initialized) {
            console.warn('⚠️  AvailabilityService: Firebase not initialized, skipping Firestore load');
          } else {
            const firestoreDb = getWebDb();
            if (!firestoreDb) {
              console.warn('⚠️  AvailabilityService: No Firebase database instance found, skipping Firestore load');
            } else {
              const bookingsSnapshot = await getDocs(collection(firestoreDb, 'bookings'));
              loadedBookings = bookingsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
              }));
              loadedFromFirestore = true;
              console.log(`✅ AvailabilityService: Loaded ${loadedBookings.length} bookings from Firestore`);
              
              // Sync to local storage for offline access
              await SimpleStorage.setItem(this.ALL_BOOKINGS_KEY, JSON.stringify(loadedBookings));
            }
          }
        } catch (firestoreError: any) {
          console.warn('⚠️  AvailabilityService: Could not load bookings from Firestore, trying local storage:', firestoreError);
        }
      }
      
      // Fallback to local storage if Firestore didn't work
      if (!loadedFromFirestore) {
        await SimpleStorage.forceInitialize();
        const bookingsJson = await SimpleStorage.getItem(this.ALL_BOOKINGS_KEY);
        if (bookingsJson) {
          loadedBookings = JSON.parse(bookingsJson);
          console.log(`📋 AvailabilityService: Loaded ${loadedBookings.length} bookings from local storage`);
        }
      }
      
      return loadedBookings;
    } catch (error) {
      console.error('AvailabilityService: Error getting bookings:', error);
      return [];
    }
  }
  
  /**
   * Save a booking to all_bookings (Firestore + local storage)
   */
  async saveBooking(booking: any): Promise<void> {
    try {
      console.log('📦 AvailabilityService: saveBooking called');
      
      // Ensure SimpleStorage is initialized
      await SimpleStorage.forceInitialize();
      
      const allBookings = await this.getAllBookings();
      allBookings.push(booking);
      
      // Save to local storage (for offline access)
      if (!SimpleStorage || typeof SimpleStorage.setItem !== 'function') {
        console.error('❌ SimpleStorage is not properly initialized!');
      } else {
        await SimpleStorage.setItem(this.ALL_BOOKINGS_KEY, JSON.stringify(allBookings));
        console.log(`✅ AvailabilityService: Booking saved to local storage`);
      }
      
      // Also save to Firestore (for cross-browser/device access)
      if (Platform.OS === 'web') {
        try {
          // Use require() to ensure same module instance as unifiedAuthService
          const { doc, setDoc } = require('firebase/firestore');
          const { getWebDb, initializeFirebase } = require('./unifiedAuthService');
          
          // Generate booking ID if not provided
          const bookingId = booking.id || `booking_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          const bookingWithId = { ...booking, id: bookingId };
          
          // Ensure Firebase is initialized and get named database
          const initialized = initializeFirebase();
          if (!initialized) {
            console.warn('⚠️  AvailabilityService: Firebase not initialized, skipping Firestore save');
          } else {
            const firestoreDb = getWebDb();
            if (!firestoreDb) {
              console.warn('⚠️  AvailabilityService: No Firebase database instance found, skipping Firestore save');
            } else {
              await setDoc(doc(firestoreDb, 'bookings', bookingId), {
                ...bookingWithId,
                createdAt: bookingWithId.createdAt || new Date().toISOString(),
                updatedAt: new Date().toISOString(),
              });
              console.log(`✅ AvailabilityService: Booking saved to Firestore: ${bookingId}`);
            }
          }
        } catch (firestoreError: any) {
          console.error('❌ AvailabilityService: Error saving booking to Firestore:', firestoreError);
          console.warn('⚠️  Booking saved locally but not in Firestore');
          // Don't throw - local save succeeded
        }
      }
    } catch (error) {
      console.error('AvailabilityService: Error saving booking:', error);
      // Don't throw - allow booking to complete even if storage fails
      console.warn('⚠️ Booking completed but not saved to storage');
    }
  }
  
  /**
   * Generate time slots with worker availability
   */
  async generateTimeSlotsWithWorkers(
    businessId: string,
    date: string,
    startHour?: number,
    endHour?: number,
    slotInterval?: number,
    businessHours?: any, // BusinessHours from BusinessOwner
    appointmentDuration?: number // Duration between appointments in minutes
  ): Promise<TimeSlotWithWorkers[]> {
    try {
      // Get day of week from date (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
      const dateObj = new Date(date + 'T00:00:00'); // Parse as local date
      const dayOfWeek = dateObj.getDay();
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const dayName = dayNames[dayOfWeek];
      
      // Use business hours if available, otherwise use defaults
      let actualStartHour = startHour || 9;
      let actualEndHour = endHour || 17;
      let actualSlotInterval = slotInterval || appointmentDuration || 30;
      
      if (businessHours && businessHours[dayName]) {
        const dayHours = businessHours[dayName];
        if (dayHours.isOpen && dayHours.openTime && dayHours.closeTime) {
          // Parse time strings (e.g., "09:00" -> hour 9)
          const [openHour, openMinute] = dayHours.openTime.split(':').map(Number);
          const [closeHour, closeMinute] = dayHours.closeTime.split(':').map(Number);
          actualStartHour = openHour;
          actualEndHour = closeHour + (closeMinute > 0 ? 1 : 0); // Round up if there are minutes
        } else {
          // Business is closed on this day
          return [];
        }
      }
      
      // Use appointment duration for slot interval if provided
      if (appointmentDuration) {
        actualSlotInterval = appointmentDuration;
      }
      
      // 1. Generate base time slots based on business hours
      const baseSlots: string[] = [];
      for (let hour = actualStartHour; hour < actualEndHour; hour++) {
        for (let minute = 0; minute < 60; minute += actualSlotInterval) {
          // Skip if this would go past closing time
          if (businessHours && businessHours[dayName]?.closeTime) {
            const [closeHour, closeMinute] = businessHours[dayName].closeTime.split(':').map(Number);
            const slotTimeInMinutes = hour * 60 + minute;
            const closeTimeInMinutes = closeHour * 60 + closeMinute;
            if (slotTimeInMinutes >= closeTimeInMinutes) {
              break;
            }
          }
          const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
          baseSlots.push(timeString);
        }
      }
      
      // 2. Get workers for this business
      let workers = await workerService.getWorkers(businessId);
      console.log(`📋 AvailabilityService: Found ${workers.length} workers for business ${businessId}`);
      
      if (workers.length === 0) {
        console.warn(`⚠️ AvailabilityService: No workers found for business ${businessId}, creating default owner worker`);
        // Create a default owner worker for solo businesses
        await workerService.initializeDefaultWorkers(businessId, 'Business Owner', true);
        workers = await workerService.getWorkers(businessId);
        console.log(`✅ AvailabilityService: Created default owner worker, now have ${workers.length} workers`);
      }

      // Guest/widget embed: Firestore may be offline — still show bookable slots.
      if (workers.length === 0) {
        workers = [
          {
            id: `owner_${businessId}`,
            name: 'Staff',
            phone: '',
            email: '',
            specialties: '',
            businessId,
            createdAt: new Date().toISOString(),
            isActive: true,
            isOwner: true,
          },
        ];
      }
      
      // 3. Get all bookings
      const allBookings = await this.getAllBookings();
      console.log(`📋 AvailabilityService: Checking ${allBookings.length} total bookings`);
      
      // 4. For each time slot, determine worker availability
      const slotsWithWorkers = baseSlots.map(timeSlot => {
        // Find all workers who are available for this time slot
        const availableWorkers = workers.filter(worker => {
          // Check if this worker has a booking at this time
          const workerIsBooked = allBookings.some(booking => 
            booking.businessId === businessId &&
            booking.appointmentDate === date &&
            booking.appointmentTime === timeSlot &&
            booking.workerId === worker.id &&
            booking.status !== 'cancelled'
          );
          
          return !workerIsBooked && worker.isActive !== false;
        });
        
        // Find bookings for this slot to count booked workers
        const bookedCount = allBookings.filter(booking =>
          booking.businessId === businessId &&
          booking.appointmentDate === date &&
          booking.appointmentTime === timeSlot &&
          booking.status !== 'cancelled'
        ).length;
        
        return {
          time: timeSlot,
          availableWorkers,
          hasAvailability: availableWorkers.length > 0,
          totalCapacity: workers.length,
          bookedCount
        };
      });
      
      // 5. Filter to only show slots with available workers
      const availableSlots = slotsWithWorkers.filter(slot => slot.hasAvailability);
      
      console.log(`✅ AvailabilityService: ${availableSlots.length} available time slots out of ${baseSlots.length}`);
      console.log(`📊 AvailabilityService: Capacity details:`, availableSlots.map(s => ({
        time: s.time,
        available: s.availableWorkers.length,
        total: s.totalCapacity
      })));
      
      return availableSlots;
      
    } catch (error) {
      console.error('AvailabilityService: Error generating time slots:', error);
      return [];
    }
  }
  
  /**
   * Check if a specific worker is available at a time
   */
  async isWorkerAvailable(
    businessId: string,
    workerId: string,
    date: string,
    time: string
  ): Promise<boolean> {
    try {
      const allBookings = await this.getAllBookings();
      
      const isBooked = allBookings.some(booking =>
        booking.businessId === businessId &&
        booking.workerId === workerId &&
        booking.appointmentDate === date &&
        booking.appointmentTime === time &&
        booking.status !== 'cancelled'
      );
      
      return !isBooked;
    } catch (error) {
      console.error('AvailabilityService: Error checking worker availability:', error);
      return false;
    }
  }
  
  /**
   * Get available workers for a specific time slot
   */
  async getAvailableWorkersForSlot(
    businessId: string,
    date: string,
    time: string
  ): Promise<Worker[]> {
    try {
      const workers = await workerService.getWorkers(businessId);
      const allBookings = await this.getAllBookings();
      
      const availableWorkers = workers.filter(worker => {
        const isBooked = allBookings.some(booking =>
          booking.businessId === businessId &&
          booking.workerId === worker.id &&
          booking.appointmentDate === date &&
          booking.appointmentTime === time &&
          booking.status !== 'cancelled'
        );
        
        return !isBooked && worker.isActive !== false;
      });
      
      return availableWorkers;
    } catch (error) {
      console.error('AvailabilityService: Error getting available workers:', error);
      return [];
    }
  }
  
  /**
   * Get booking capacity for a time slot
   */
  async getSlotCapacity(
    businessId: string,
    date: string,
    time: string
  ): Promise<{ total: number; booked: number; available: number }> {
    try {
      const workers = await workerService.getWorkers(businessId);
      const allBookings = await this.getAllBookings();
      
      const bookedCount = allBookings.filter(booking =>
        booking.businessId === businessId &&
        booking.appointmentDate === date &&
        booking.appointmentTime === time &&
        booking.status !== 'cancelled'
      ).length;
      
      return {
        total: workers.length,
        booked: bookedCount,
        available: workers.length - bookedCount
      };
    } catch (error) {
      console.error('AvailabilityService: Error getting slot capacity:', error);
      return { total: 0, booked: 0, available: 0 };
    }
  }
}

export default new AvailabilityService();

